import {
  throwNotInstance,
  throwNotType,
  throwNull,
  throwUndefined
} from '@esm-test/guards';
import { getChildGroupName } from '../../processors/pre/preProcessUtils.js';
import { TestType } from './testType.js';

/**
 * Represents a discovered test or test group.
 */
export class Test {
  /** The name of the group this test belongs to. */
  groupName: string;
  /** The name of the test or group. */
  name: string;
  /** The actual test function, class instance, or object. */
  value: any;
  /** The bitwise type information for the test. */
  type: TestType;
  /** Parameterized test cases, if any. */
  testCases: any[];
  /** The expression used to determine which tests to run (for 'only' functionality). */
  onlyExpression: any;

  /**
   * Initializes a new Test instance.
   * @param groupName - The parent group name.
   * @param name - The test name.
   * @param value - The test implementation or object.
   * @param type - The bitwise type flags.
   * @param testCases - Parameterized test cases.
   * @param onlyExpression - Expression for 'only' filtering.
   */
  constructor (groupName: string, name: string, value: any, type: TestType, testCases: any[], onlyExpression: any) {
    throwNotType("groupName", groupName, "string");
    throwNotType("name", name, "string");

    throwUndefined("value", value);
    throwNull("value", value);

    throwNotInstance("type", type, TestType);
    throwNotInstance("testCases", testCases, Array);

    throwUndefined("onlyExpression", onlyExpression);
    throwNull("onlyExpression", onlyExpression);

    this.groupName = groupName;
    this.name = name;
    this.value = value;
    this.type = type;
    this.testCases = testCases;
    this.onlyExpression = onlyExpression;
  }

  /**
   * Whether this test is marked as 'only'.
   */
  get isOnly(): boolean {
    return this.type.has(TestType.HAS_ONLY);
  }

  /**
   * Whether this entity represents a test group (e.g., a module, class, or object).
   */
  get isGroup(): boolean {
    return this.type.isEither(
      TestType.EXPORT_DEFAULT,
      TestType.MODULE,
      TestType.OBJECT,
      TestType.OBJECT_WITH_ONLY,

      TestType.CLASS_DEF,
      TestType.CLASS_DEF_EXPORT_DEFAULT,
      TestType.CLASS_DEF_WITH_TITLE,
      TestType.CLASS_DEF_WITH_ONLY,

      TestType.CLASS_INST
    );
  }

  /**
   * The unique key identifying this test, combining group name and name.
   */
  get key(): string {
    return getChildGroupName(this.groupName, this.name);
  }

}
